import { Injectable } from "@angular/core";
import { BehaviorSubject, of } from "rxjs";
import { Expert, ExpertStatus } from "../../models/expert.model";
import { ExpertTime } from "../../models/expert-time.model";
import { ExpertTimeList } from "../../models/expert-time-list.model";
import * as moment from 'moment-timezone';
import * as momentTZ from "moment-timezone";
import * as utils from '../../utils/utility-functions';
import { Specialty } from "../../models/specialty.model";
import { SubSpecialty } from "../../models/sub-specialty.model";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { AngularFireStorage } from "@angular/fire/compat/storage";
import { EmailNotificationService } from "../notification.service";
import { UserTypeEnum } from "src/app/models/user-type";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { switchMap } from "rxjs/operators";
import { AngularFireAuth } from "@angular/fire/compat/auth";


@Injectable({
  providedIn: 'root',
})

export class ExpertService {
  private _DISABLE_ACCOUNT = 'disableAccount';
  private _ENABLE_ACCOUNT = 'enableAccount';
  _OPEN = 'open';

  private firebaseAppointmentsCollection: AngularFirestoreCollection;
  private firebaseInvitedCollection: AngularFirestoreCollection;
  private firebaseExpertCollection: AngularFirestoreCollection;
  private firebaseExpertSubSpecialtyCollection: AngularFirestoreCollection;
  private firebaseExpertSpecialtyCollection: AngularFirestoreCollection;
  private firebaseExpertAvailabilityCollection: AngularFirestoreCollection;
  private firebaseOnCallScheduleCollection: AngularFirestoreCollection;
  private firebaseOnCallWeeklyScheduleCollection: AngularFirestoreCollection;


  private experts = new BehaviorSubject([]);
  currentExperts = this.experts.asObservable();

  private userProfile = new BehaviorSubject(null);
  currentUserProfile = this.userProfile.asObservable();

  private allExperts = new BehaviorSubject([]);
  currentAllExperts = this.allExperts.asObservable();

  private pendingExperts = new BehaviorSubject([]);
  currentPendingExperts = this.pendingExperts.asObservable();

  private expertProfileImage = new BehaviorSubject(null);
  currentExpertProfileImage = this.expertProfileImage.asObservable();

  private expert = new BehaviorSubject(null);
  currentExpert = this.expert.asObservable();

  private expertTimeList = new BehaviorSubject([]);
  currentExpertTimeList = this.expertTimeList.asObservable();

  private expertSpecialityList = new BehaviorSubject([]);
  currentExpertSpecialityList = this.expertSpecialityList.asObservable();

  constructor(
    private angularFirestore: AngularFirestore,
    private emailNotificationService: EmailNotificationService,
    private angularFunctions: AngularFireFunctions,
    private storage: AngularFireStorage,
    private afAuth: AngularFireAuth) {
    const expertUser = localStorage.getItem('expertUser');
    this.expert.next(JSON.parse(expertUser));
    this.firebaseInvitedCollection = this.angularFirestore.collection('Invited');
  }

  async getExpertInformation(userID: string) {
    try {
      let expert = null;

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      await this.firebaseExpertCollection.ref
        .doc(userID)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          expert = docData;
        }).catch(err => console.log("getExpertInformation query 1 error: " + err));

      this.updateSingleExpert(expert);
      return expert;
    } catch (error) {
      console.log('getExpertInformation(): ' + JSON.stringify(error));
    }
  }

  // Create
  async createExpert(expert: any) {
    try {
      let newExpert = utils.createNewExpertObject(expert);
      newExpert.is_setup = false;
      const doctorRef = (await this.firebaseInvitedCollection.doc(expert.email).ref.get()).exists;
      if (doctorRef) { return false };

      if (expert.sub_specialty == null) {
        await this.firebaseInvitedCollection.doc(newExpert.email.toLowerCase().trim()).set({
          ...newExpert,
          type: UserTypeEnum.EXPERT,
          accepted: false,
          created_at: Number(moment().format('x'))
        });
      } else {
        await this.firebaseInvitedCollection.doc(newExpert.email).set({
          ...newExpert,
          type: UserTypeEnum.EXPERT,
          accepted: false,
          general_sub_specialty: expert.sub_specialty.general_sub_specialty,
          specialty: expert.specialty.specialty_name,
          specialty_id: expert.specialty.id,
          sub_specialty: expert.sub_specialty.sub_specialty_name,
          sub_specialty_id: expert.sub_specialty.id,
          created_at: Number(moment().format('x'))
        });
      }

      this.emailNotificationService.sendExpertInvitationEmail(newExpert.email, `${newExpert.first_name} ${newExpert.last_name}`);

      return true;
    } catch (error) {
      console.log('createExpert', error);
      return false;
    }
  }

  async updateExpert(expert: any) {
    try {

      let { id, ...expertToSave } = expert;
      expertToSave.updated_at = Number(moment().format('x'));
      expertToSave.first_name = expertToSave.first_name.trim().toLowerCase();
      expertToSave.last_name = expertToSave.last_name.trim().toLowerCase();

      await this.firebaseExpertCollection.ref
        .doc(id)
        .update(JSON.parse(JSON.stringify(expertToSave)))
        .catch(err => console.log("updateExpert query 1 error: " + err));

      expertToSave.id = id;
      this.updateSingleExpert(expertToSave);

      return true;
    } catch (error) {
      console.log('createExpert', error);
      return false;
    }
  }

  async saveSpecialtyToExpert(specialty: Specialty, subSpecialty: SubSpecialty, expertID: string) {
    try {
      if (this.firebaseExpertSubSpecialtyCollection == null) {
        this.firebaseExpertSubSpecialtyCollection = this.angularFirestore.collection('ExpertSubSpecialties');
      }

      let newSubSpecialty: any = utils.createSubSpecialtyObject(specialty, subSpecialty, expertID);

      await this.firebaseExpertSubSpecialtyCollection
        .add(JSON.parse(JSON.stringify(newSubSpecialty)))
        .then(function (docRef) {
          newSubSpecialty.id = docRef.id;
        })
        .catch(err => console.log("saveSpecialtyToExpert save 1 error: " + err));

      const createdSubSpecialty = utils.createSubSpecialty(newSubSpecialty);
      return createdSubSpecialty;
    } catch (error) {
      console.log(error);
    }
  }

  async createZoomMeeting(appointment: any) {
    try {
      const callable = this.angularFunctions.httpsCallable('createZoomMeeting');

      return callable({
        appointment: appointment
      })
        .toPromise()
        .then((result) => {
          return result;
        })
        .catch((error) => {
          return null;
        }
        );
    } catch (error) {
      console.log('createZoomMeeting(): ' + JSON.stringify(error));
    }
  }

  // Get
  async getExpertIDsBySpecialties(specialty: Specialty, subSpecialty: SubSpecialty) {
    try {
      let subSpecialties = [];

      if (this.firebaseExpertSubSpecialtyCollection == null) {
        this.firebaseExpertSubSpecialtyCollection = this.angularFirestore.collection('ExpertSubSpecialties');
      }

      if (specialty.id != 'undefined' && subSpecialty.id != 'undefined') {

        if (specialty.id == '0') {
          await this.firebaseExpertSubSpecialtyCollection.ref
            .where('sub_specialty_id', '==', subSpecialty.id)
            .get()
            .then(querySnapshot => {
              querySnapshot.forEach(doc => {
                const docData = {
                  id: doc.id,
                  ...doc.data()
                }
                subSpecialties.push(docData)
              });
            }).catch(err => console.log("getExpertIDsBySpecialties query 1 error: " + err));
        } else {
          await this.firebaseExpertSubSpecialtyCollection.ref
            .where('specialty_id', '==', specialty.id)
            .where('sub_specialty_id', '==', subSpecialty.id)
            .get()
            .then(querySnapshot => {
              querySnapshot.forEach(doc => {
                const docData = {
                  id: doc.id,
                  ...doc.data()
                }
                subSpecialties.push(docData)
              });
            }).catch(err => console.log("getExpertIDsBySpecialties query 2 error: " + err));
        }
      }

      if (subSpecialties.length == 0) {
        await this.firebaseExpertSubSpecialtyCollection.ref
          .where('specialty_id', '==', specialty.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.forEach(doc => {
              const docData = {
                id: doc.id,
                ...doc.data()
              }
              subSpecialties.push(docData)
            });
          }).catch(err => console.log("getExpertIDsBySpecialties query 3 error: " + err));

        this.updateExpertIDsBySpecialties(subSpecialties);
        return subSpecialties;
      } else {
        this.updateExpertIDsBySpecialties(subSpecialties);
        return subSpecialties;
      }
    } catch (error) {
      console.log(error);
    }
  }

  async getAllExperts() {
    try {
      let experts = [];
      this.allExperts.next([]);

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      await this.firebaseExpertCollection.ref
        .orderBy('last_name')
        .orderBy('first_name')
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            experts.push(docData)
          });
        }).catch(err => console.log("getAllExperts query 1 error: " + err));

      this.updateAllExperts(experts);
      return experts;
    } catch (error) {
      console.log(error);
    }
  }

  async getAllInvitedExperts() {
    try {
      let experts = [];
      this.allExperts.next([]);

      if (this.firebaseInvitedCollection == null) {
        this.firebaseInvitedCollection = this.angularFirestore.collection('Invited');
      }

      await this.firebaseInvitedCollection.ref
        .where('type', '==', 1)
        .where('accepted', '==', false)
        .orderBy('last_name')
        .orderBy('first_name')
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            experts.push(docData)
          });
        }).catch(err => console.log("getAllExperts query 1 error: " + err));

      this.updateAllExperts(experts);
      return experts;
    } catch (error) {
      console.log(error);
    }
  }

  async getPendingExperts() {
    try {
      let pendingList = [];

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      await this.firebaseExpertCollection.ref
        .where('status', '==', ExpertStatus.PENDING)
        .orderBy('last_name')
        .orderBy('first_name')
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            pendingList.push(docData)
          });
        }).catch(err => console.log("getPendingExperts query 1 error: " + err));

      this.updatePendingExperts(pendingList);
      return pendingList;
    } catch (error) {
      console.log(error);
    }
  }

  async getExpertSpecialties(expert: Expert) {
    try {
      let expertSpecialties = [];

      if (this.firebaseExpertSubSpecialtyCollection == null) {
        this.firebaseExpertSubSpecialtyCollection = this.angularFirestore.collection('ExpertSubSpecialties');
      }

      await this.firebaseExpertSubSpecialtyCollection.ref
        .where('expert_id', '==', expert.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            expertSpecialties.push(docData)
          });
        }).catch(err => console.log("getExpertSpecialties query 1 error: " + err));

      this.updateExpertSpecialtyInfo(expertSpecialties);
      return expertSpecialties;
    } catch (error) {
      console.log(error);
    }
  }

  private async getExpertSpecialityInfo(expert: Expert) {
    try {
      let expertSpecialties = [];

      if (this.firebaseExpertSubSpecialtyCollection == null) {
        this.firebaseExpertSubSpecialtyCollection = this.angularFirestore.collection('ExpertSubSpecialties');
      }

      await this.firebaseExpertSubSpecialtyCollection.ref
        .where('expert_id', '==', expert.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            expertSpecialties.push(docData)
          });
        }).catch(err => console.log("getExpertSpecialityInfo query 1 error: " + err));

      return expertSpecialties;
    } catch (error) {
      console.log(error);
    }
  }

  async getExpertByUserID(id: string) {
    try {
      let expert = null;

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      await this.firebaseExpertCollection.ref
        .doc(id)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          expert = docData;
        }).catch(err => console.log("getExpertByID query 1 error: " + err));

      this.updateProfileUser(expert);
      return expert;
    } catch (error) {
      console.log(error);
    }
  }

  async getExpertByID(id: string) {
    try {
      let expert = null;

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      await this.firebaseExpertCollection.ref
        .doc(id)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          expert = docData;
        }).catch(err => console.log("getExpertByID query 1 error: " + err));

      return expert;
    } catch (error) {
      console.log(error);
    }
  }

  private async getExpertsByID(ids: string[]) {
    try {
      let experts = [];
      let idPromises = [];

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      ids.map((currendID: any) => {
        const promiseCreated = new Promise((resolve, reject) => {
          resolve(this.firebaseExpertCollection.ref
            .doc(currendID)
            .get());
        }).then((querySnapshot: any) => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }

          if (docData.status != null && docData.status == ExpertStatus.ACTIVE) {
            experts.push(docData)
          }
        });
        idPromises.push(promiseCreated);
      })

      await Promise.all(idPromises);

      this.updateExpertsByID(experts);
      return experts;
    } catch (error) {
      console.log('getExpertsByID(): ' + JSON.stringify(error));
    }
  }

  async getSingleExpertById(expertID: string) {
    try {
      let expert = null;

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      await this.firebaseExpertCollection.ref
        .doc(expertID)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          expert = docData;
        }).catch(err => console.log("getSingleExpertById query 1 error: " + err));

      this.updateSingleExpert(expert);
      return expert;
    } catch (error) {
      console.log(error);
    }
  }

  getSingleExpertByIdAsync(expertID: string) {
    return this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.angularFirestore.collection<Expert>('Experts')
            .doc(expertID)
            .valueChanges({ idField: 'id' });
        } else {
          return of(null);
        }
      })
    );
  }

  async getExpertAvailability(expertID: string) {
    try {
      this.updateExpertAvailability(null);
      let availabilitySchedule: any[] = [];

      if (this.firebaseExpertAvailabilityCollection == null) {
        this.firebaseExpertAvailabilityCollection = this.angularFirestore.collection('ExpertAvailability');
      }

      await this.firebaseExpertAvailabilityCollection.ref
        .where('expert_id', '==', expertID)
        .where('available', '==', true)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            availabilitySchedule.push(docData)
          });
        }).catch(err => console.log("getExpertAvailability query 1 error: " + err));

      this.updateExpertAvailability(availabilitySchedule);
      return availabilitySchedule;
    } catch (error) {
      console.log(error);
    }
  }

  async getExpertsBySpecialtyID(specialty_id) {
    try {
      let expertSpecialties = [];

      if (this.firebaseExpertSubSpecialtyCollection == null) {
        this.firebaseExpertSubSpecialtyCollection = this.angularFirestore.collection('ExpertSubSpecialties');
      }

      await this.firebaseExpertSubSpecialtyCollection.ref
        .where('specialty_id', '==', specialty_id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            expertSpecialties.push(docData)
          });
        }).catch(err => console.log("getExpertsBySpecialtyID query 1 error: " + err));

      this.updateExpertsBySpecialty(expertSpecialties);
      return expertSpecialties;
    } catch (error) {
      console.log(error);
    }
  }

  async getDaySchedule(timeDate: any) {
    try {
      let availabilitySchedule: any[] = [];

      if (this.firebaseExpertAvailabilityCollection == null) {
        this.firebaseExpertAvailabilityCollection = this.angularFirestore.collection('ExpertAvailability');
      }

      let useTime = moment(timeDate).year(moment().year()).format();

      const expertID = this.expert.getValue().id;

      let startDateTemp = moment(useTime).startOf("day").format('x');
      let endDateTemp = moment(useTime).endOf("day").format('x');

      await this.firebaseExpertAvailabilityCollection.ref
        .where('expert_id', '==', expertID)
        .where('timestamp', '>=', startDateTemp)
        .where('timestamp', '<=', endDateTemp)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            availabilitySchedule.push(docData)
          });
        }).catch(err => console.log("getDaySchedule query 1 error: " + err));

      // Get and return times
      const getTimes = this.sortDay(availabilitySchedule);
      return getTimes;
    } catch (error) {
      console.log('getDaySchedule(): ' + JSON.stringify(error));
    }
  }

  async getDayScheduleByExpertID(timeDate: any, expertID: string) {
    try {
      let availabilitySchedule: any[] = [];

      if (this.firebaseExpertAvailabilityCollection == null) {
        this.firebaseExpertAvailabilityCollection = this.angularFirestore.collection('ExpertAvailability');
      }

      let useTime = moment(timeDate).year(moment().year()).format();

      let startDateTemp = moment(useTime).startOf("day").format('x');
      let endDateTemp = moment(useTime).endOf("day").format('x');

      await this.firebaseExpertAvailabilityCollection.ref
        .where('expert_id', '==', expertID)
        .where('timestamp', '>=', startDateTemp)
        .where('timestamp', '<=', endDateTemp)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            availabilitySchedule.push(docData)
          });
        }).catch(err => console.log("getDaySchedule query 1 error: " + err));

      // Get and return times
      const getTimes = this.sortDay(availabilitySchedule);
      return getTimes;
    } catch (error) {
      console.log('getDaySchedule(): ' + JSON.stringify(error));
    }
  }

  // Remove
  async removeSpecialtyFromExpertByID(id: string) {
    try {
      if (this.firebaseExpertSubSpecialtyCollection == null) {
        this.firebaseExpertSubSpecialtyCollection = this.angularFirestore.collection('ExpertSubSpecialties');
      }

      await this.firebaseExpertSubSpecialtyCollection.ref
        .doc(id)
        .delete()
        .catch(err => console.log("removeSpecialtyFromExpertByID delete 1 error: " + err));

    } catch (error) {
      console.log(error);
    }
  }

  // Update
  async updateExpertAvailabilityTimes(time: any) {
    try {
      if (this.firebaseExpertAvailabilityCollection == null) {
        this.firebaseExpertAvailabilityCollection = this.angularFirestore.collection('ExpertAvailability');
      }

      await this.firebaseExpertAvailabilityCollection.ref
        .doc(time.id)
        .update({
          available: false,
          booked: true,
        });

    } catch (error) {
      console.log(error);
    }
  }

  async updateExpertAvailabilityTime(time: any, isAvailable: boolean) {
    try {
      if (this.firebaseExpertAvailabilityCollection == null) {
        this.firebaseExpertAvailabilityCollection = this.angularFirestore.collection('ExpertAvailability');
      }

      await this.firebaseExpertAvailabilityCollection.ref
        .doc(time.id)
        .update({
          available: isAvailable ? true : false,
          booked: isAvailable ? false : true,
        });

    } catch (error) {
      console.log(error);
    }
  }

  // Async function to save image file to firebase storage
  async saveExpertProfileImage(image: any) {
    try {
      let downloadURL;

      const fileName = moment().format('x').toString() + '_' + image.name;
      const remotePath = 'uploads/images/' + fileName;
      const fileRef = this.storage.ref(remotePath)

      await this.storage.upload(remotePath, image)
        .catch((error) => {
          console.log('saveImage upload error', error)
          return null;
        });

      await fileRef.getDownloadURL().toPromise()
        .then((result) => {
          downloadURL = result;
        })
        .catch((error) => {
          console.log('saveImage getDownloadURL error', error)
          return null;
        });

      return downloadURL;
    } catch (error) {
      console.log(error);
    }
  }

  async checkUpcomingAppointmentsForExpert(expertID: string) {
    try {

      if (this.firebaseAppointmentsCollection == null) {
        this.firebaseAppointmentsCollection = this.angularFirestore.collection('Appointments');
      }

      // create a timestamp starting from now, check Appointemnts collectio where expert_id is the expert id and the timestamp is greater than or equal to the timestamp created
      // limit to 1, if there is a document, return true, else return false
      const now = moment().format('x');
      const querySnapshot: any = await this.firebaseAppointmentsCollection.ref
        .where('expert_id', '==', expertID)
        .where('appointment_date_timestamp', '>=', now)
        .where('consultation', 'array-contains', this._OPEN)
        .limit(1)
        .get()
        .catch(err => console.log("checkUpcomingAppointmentsForExpert query 1 error: " + err));

      if (querySnapshot.size > 0) {
        return false;
      }

      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  async updateExpertStatus(expert: Expert, status: any) {
    try {
      let onCallListOfSpecialtyIds = [];
      let onCallListOfScecialtyNames = [];
      let expertSubSpecialties = [];


      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      if (this.firebaseOnCallScheduleCollection == null) {
        this.firebaseOnCallScheduleCollection = this.angularFirestore.collection('OnCallSchedule');
      }

      if (this.firebaseExpertSpecialtyCollection == null) {
        this.firebaseExpertSpecialtyCollection = this.angularFirestore.collection('Specialty');
      }

      if (this.firebaseExpertSubSpecialtyCollection == null) {
        this.firebaseExpertSubSpecialtyCollection = this.angularFirestore.collection('ExpertSubSpecialties');
      }

      if (this.firebaseOnCallWeeklyScheduleCollection == null) {
        this.firebaseOnCallWeeklyScheduleCollection = this.angularFirestore.collection('OnCallWeeklySchedule');
      }

      if (status == ExpertStatus.DISABLED) {
        // get monday of this week timestamp starting from 12am pacific time
        const begginingOfWeekMonday = momentTZ.tz(new Date(), "America/Los_Angeles").startOf('isoWeek').format();
        // Monday
        let monday = Number(momentTZ.tz(begginingOfWeekMonday, "America/Los_Angeles").format('x'));

        // search the collection OnCallSchedule where the expert_id is the expert id and the timestamp is greater than or equal to monday, save specialty_id in array if not already in array
        await this.firebaseOnCallScheduleCollection.ref
          .where('expert_id', '==', expert.id)
          .where('timestamp', '>=', monday)
          .get()
          .then(querySnapshot => {
            querySnapshot.forEach(doc => {
              const docData: any = {
                id: doc.id,
                ...doc.data()
              }

              if (!onCallListOfSpecialtyIds.includes(docData?.specialty_id)) {
                onCallListOfSpecialtyIds.push(docData?.specialty_id);
              }
            });
          }).catch(err => console.log("updateExpertStatus query 1 error: " + err));

        // search the collection ExpertSubSpecialties where the expert_id is the expert id, save specialty_id in array if not already in array
        await this.firebaseExpertSubSpecialtyCollection.ref
          .where('expert_id', '==', expert.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.forEach(doc => {
              const docData: any = {
                id: doc.id,
                ...doc.data()
              }

              if (!expertSubSpecialties.includes(docData?.specialty_id)) {
                expertSubSpecialties.push(docData?.specialty_id);
              }
            });
          }).catch(err => console.log("updateExpertStatus query 3 error: " + err));

        const weeklySchedulePromises = expertSubSpecialties.map(async (specialty_id) => {

          const querySnapshot: any = await this.firebaseOnCallWeeklyScheduleCollection.ref
            .where('specialty_id', '==', specialty_id)
            .get()
            .catch(err => console.log("updateExpertStatus query 4 error: " + err));

          // loop through each document, loop through field week, check each object id against expert id, if match, add to array
          querySnapshot.forEach(doc => {
            const docData: any = {
              id: doc.id,
              ...doc.data()
            }

            const weekArray = Object.values(docData.week);

            weekArray.forEach((day: any) => {
              if (day?.id == expert.id) {
                if (!onCallListOfSpecialtyIds.includes(docData.specialty_id)) {
                  console.log('not in there!', docData.specialty_id)
                  onCallListOfSpecialtyIds.push(docData.specialty_id);
                }
              }
            });

          });
        });

        await Promise.all(weeklySchedulePromises);

        const promises = onCallListOfSpecialtyIds.map(async (specialty_id) => {
          const querySnapshot: any = await this.firebaseExpertSpecialtyCollection.ref
            .doc(specialty_id)
            .get()
            .catch(err => console.log("updateExpertStatus query 2 error: " + err));

          const docData: any = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }

          if (!onCallListOfScecialtyNames.includes(docData?.specialty_name)) {
            onCallListOfScecialtyNames.push(docData?.specialty_name);
          }
        });

        await Promise.all(promises);

        if (onCallListOfScecialtyNames.length > 0) {
          return { success: false, data: onCallListOfScecialtyNames };
        }
      }


      await this.firebaseExpertCollection.ref
        .doc(expert.id)
        .update({
          status: status,
          updated_at: moment().format('x')
        });

      expert.status = status;
      this.updateSingleExpert(expert);

      let callableString = '';

      if (status == ExpertStatus.DISABLED) {
        callableString = this._DISABLE_ACCOUNT;
      } else if (status == ExpertStatus.ACTIVE) {
        callableString = this._ENABLE_ACCOUNT;
      }

      const callable = this.angularFunctions.httpsCallable(callableString);

      callable({
        user_id: expert.id,
      })
        .toPromise()
        .then((result) => console.log('test', result))
        .catch((error) => console.log('test', error));

      return { success: true, data: null }

    } catch (error) {
      console.log(error);
    }
  }

  async updateExpertPhoneNumber(expertID: string, phone: string) {
    try {
      const callable = this.angularFunctions.httpsCallable('updateUserPhoneNumber');

      await callable({
        user_id: expertID,
        phone: phone
      })
        .toPromise()

      if (this.firebaseExpertCollection == null) {
        this.firebaseExpertCollection = this.angularFirestore.collection('Experts');
      }

      await this.firebaseExpertCollection.ref
        .doc(expertID)
        .update({
          phone: phone,
          updated_at: moment().format('x')
        }).catch(err => console.log("updateExpertPhoneNumber update 1 error: " + err));

      return true;

    } catch (error) {
      console.log(error);
      return false;
    }
  }

  updateProfileUser(userProfile: any) {
    const updatedProfile = utils.createExpert(userProfile);
    this.userProfile.next(updatedProfile);
  }

  updateProfileImage(imageURL: string) {
    this.expertProfileImage.next(imageURL);
  }

  updateExpertSpecialtyInfo(specialtyInfo: any) {
    let newSubSpecialtyList = [];
    specialtyInfo.forEach(specialty => {
      const newSpecialty = utils.createSubSpecialty(specialty);
      newSubSpecialtyList.push(newSpecialty);
    });

    this.expertSpecialityList.next(newSubSpecialtyList);
  }

  updateExpertAvailability(expertAvailability: any) {
    if (expertAvailability == null) {
      this.expertTimeList.next([]);
    } else {
      const availableTimeList: ExpertTime[] = [];
      const todaysDate = new Date();
      for (let i = 0; i < expertAvailability.length; i++) {
        const currentDate = new Date(expertAvailability[i].date_time);
        if (expertAvailability[i].available && currentDate >= todaysDate) {
          const newTime = new ExpertTime;
          newTime.id = expertAvailability[i].id;
          newTime.date_time = new Date(expertAvailability[i].date_time);
          availableTimeList.push(newTime);
        }
      }

      let sortedTimes = availableTimeList.sort((time1, time2) => (time1.date_time.getTime() > time2.date_time.getTime()) ? 1 : (time1.date_time.getTime() < time2.date_time.getTime()) ? -1 : 0);
      let daysList = [];
      let expertTimeList: ExpertTimeList[] = [];

      for (let i = 0; i < sortedTimes.length; i++) {
        const currentDay = sortedTimes[i].date_time.getDate();
        if (!daysList.includes(currentDay)) {
          daysList.push(currentDay);
          const newTimeList = new ExpertTimeList;
          newTimeList.dayOfWeek = utils.getDayString(sortedTimes[i].date_time.getDay());
          newTimeList.month = utils.getLongMonthString(sortedTimes[i].date_time.getMonth());
          newTimeList.date = sortedTimes[i].date_time.getDate().toString();

          const newTime = new ExpertTime;
          newTime.id = sortedTimes[i].id;

          newTime.date_time = sortedTimes[i].date_time;
          newTime.time = moment(sortedTimes[i].date_time).format('HH').toString() + ':' + moment(sortedTimes[i].date_time).format('mm').toString();
          newTime.timeString = newTimeList.dayOfWeek.toString() + ", " + newTimeList.month.toString() + " " + newTimeList.date.toString();


          newTimeList.availableTimes.push(newTime);
          expertTimeList.push(newTimeList);
        } else {
          let expertTimeListLength = expertTimeList.length;

          const newTime = new ExpertTime;
          newTime.id = sortedTimes[i].id;
          newTime.date_time = sortedTimes[i].date_time;
          newTime.time = moment(sortedTimes[i].date_time).format('kk:mm').toString();;
          newTime.timeString = expertTimeList[expertTimeListLength - 1].dayOfWeek.toString() + ", " + expertTimeList[expertTimeListLength - 1].month.toString() + " " + expertTimeList[expertTimeListLength - 1].date.toString();

          expertTimeList[expertTimeListLength - 1].availableTimes.push(newTime);
        }
      }
      this.expertTimeList.next(expertTimeList);
    }

  }

  updateSingleExpert(expert: any) {
    if (typeof expert !== 'undefined') {
      let newExpert = utils.createExpert(expert);
      // this.selectedExpertTemp = newExpert;
      this.expert.next(newExpert);
      localStorage.setItem("expertUser", JSON.stringify(newExpert));
    }
  }

  updateAllExperts(expertCollectionResults: any) {
    let expertsTemp: Expert[] = [];
    for (let i = 0; i < expertCollectionResults.length; i++) {
      let newExpert = utils.createExpert(expertCollectionResults[i]);
      expertsTemp.push(newExpert);
    }
    if (expertsTemp.length > 0) {
      expertsTemp.forEach(expert => {
        this.getExpertSpecialityInfo(expert);
      });
      this.allExperts.next(expertsTemp);
    }
  }

  updatePendingExperts(expertCollectionResults: any) {
    let expertsTemp: Expert[] = [];
    for (let i = 0; i < expertCollectionResults.length; i++) {
      let newExpert = utils.createExpert(expertCollectionResults[i]);
      expertsTemp.push(newExpert);
    }
    if (expertsTemp.length > 0) {
      expertsTemp.forEach(expert => {
        this.getExpertSpecialityInfo(expert);
      });
      this.pendingExperts.next(expertsTemp);
    }
  }

  updateExpertsBySpecialty(specialtieData) {
    let expertIDs = [];

    specialtieData.forEach(element => {
      expertIDs.indexOf(element.expert_id) === -1 ? expertIDs.push(element.expert_id) : '';
    });

    this.getExpertsByID(expertIDs);
  }

  updateExpertsByID(expertCollectionResults: any) {
    let experts = [];
    expertCollectionResults.forEach(expert => {
      let newExpert = utils.createExpert(expert);
      experts.push(newExpert);
    });

    this.experts.next(experts);
  }

  updateExpertIDsBySpecialties(expertCollectionResults) {
    let expertIDs = [];

    expertCollectionResults.map((expert) => {
      expertIDs.indexOf(expert.expert_id) === -1 ? expertIDs.push(expert.expert_id) : '';
    });
    this.getExpertsByID(expertIDs);
  }

  // Helper methods
  private sortDay(weekData: any) {
    let times = [];

    weekData.sort((a, b) => {
      return a.timestamp - b.timestamp;
    });

    weekData.forEach(element => {
      let tempTime = {
        id: element.id,
        date_time: element.date_time,
        available: element.available,
        expert_id: element.expert_id,
        timestamp: element.timestamp,
        booked: element.booked
      };
      times.push(tempTime);
    });

    return times;
  }
}